import Reservation from "@/types/clients/esquire/data/general/reservation";
import Contract from "@/types/clients/esquire/data/general/contract";
import CrmClient from "@/types/clients/esquire/data/general/crm-client";
import ROLE from "@/types/enums/role";
import {PendingPaymentContext} from "@/stores/session/types";

/**
 * Snapshot of the **persisted** session store.
 * Only fields listed here are serialized to localStorage.
 * All other runtime fields/refs are reconstructed at runtime.
 *
 * Notes:
 * - `expires` is a UNIX timestamp in **seconds**.
 * - `routes` are client-owned maps provided by the backend payloads and
 *   kept in the persist layer for navigation continuity.
 */
export type PersistSnapshot = {
  role?: ROLE;
  signature?: string | null;
  expires?: number | null;
  crmId?: number | null;
  language?: string;
  contractId?: string | null;
  pendingPayment?: PendingPaymentContext | null;
};

// --- Whole-store mappers ---

/**
 * Convert the in-memory session store to a persistable snapshot.
 * Only returns whitelisted fields (safe for localStorage).
 */
export function toPersistSnapshot(s: {
  role: ROLE;
  reservation: Reservation | null;
  reservations: Reservation[] | undefined; // tolerate undefined
  contract: Contract | null;
  crmClient: CrmClient | null;
  signature: string | null;
  expires: number | null;
  crmId: number | null;
  language: string;
  contractId: string | null;
  pendingPayment: unknown | null;
}): PersistSnapshot {
  return {
    role: s.role,
    signature: s.signature,
    expires: s.expires,
    crmId: s.crmId,
    language: s.language,
    contractId: s.contractId ?? null,
    pendingPayment: (s.pendingPayment as any) ?? null
  };
}

export function fromPersistSnapshot(p: PersistSnapshot): Partial<{
  role: ROLE;
  reservation: Reservation | null;
  reservations: Reservation[];
  contract: Contract | null;
  crmClient: CrmClient | null;
  signature: string | null;
  expires: number | null;
  crmId: number | null;
  language: string;
  contractId: string | null;
  pendingPayment: unknown | null;
}> {
  return {
    role: p.role ?? ROLE.ANONYMOUS,
    signature: p.signature ?? null,
    expires: p.expires ?? null,
    crmId: p.crmId ?? null,
    language: p.language ?? "en",
    contractId: p.contractId ?? null,
    pendingPayment: p.pendingPayment ?? null
  };
}

